import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Button,
  Dropdown,
  Input,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Option,
} from '@fluentui/react-components';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { ArrowRight, CheckCircle2, Folder, Laptop, PlayCircle, Plus, RefreshCw, Search } from 'lucide-react';

import {
  GetMigrationsParams,
  Migration,
  MigrationsListResponse,
  getMigrations,
} from '../api/migrations';
import { getMigrationGroups } from '../api/migrationGroups';
import { DataTable, DataTableColumn } from '../components/DataTable';
import RelativeTime from '../components/RelativeTime';
import TenantLabel from '../components/TenantLabel';
import GroupLabel from '../components/GroupLabel';
import {
  HeroBand,
  MetricCard,
  MetricRow,
  SectionPanel,
  StatePill,
} from '../components/abPrimitives';
import { deriveCampaignStatus } from '../lib/campaignStatus';
import { migrationTypeLabel } from '../lib/vocabulary';
import { IDENTIFIER, STROKE, TEXT } from '../theme/tokens';

// Every campaign, across every group.
//
// FD-467 — the AirBridge design over the capability this page already had. The
// preview loaded ONE page of 100 with a client-side search, which was right for
// a preview and wrong here: the infinite pagination, the wave filter and the
// URL-held group filter are all real operator capability and none of them is
// dropped. What the port takes from the preview is the hero, the metric row,
// the panel and the state pill.

// Sentinel for the unfiltered state. `none` is a real server value, so the
// "no filter" option needs its own token rather than reusing an empty string.
const GROUP_FILTER_ALL = '__all__';

const PAGE_SIZE = 50;

export default function MigrationsListPage() {
  const navigate = useNavigate();

  const [waveInput, setWaveInput] = useState('');
  const [waveFilter, setWaveFilter] = useState<string | undefined>(undefined);

  // FD-262/FD-266 — the group filter rides the URL (`?group=<id>` / `?group=none`)
  // rather than component state, so it survives reload, is shareable, and
  // deep-links restore the control on mount. NEVER localStorage — an operator-tool
  // filter that reappears silently on a later visit is a trap.
  const [searchParams, setSearchParams] = useSearchParams();
  const groupFilter = searchParams.get('group') ?? undefined;

  const groupsQuery = useQuery({
    queryKey: ['migration-groups'],
    queryFn: getMigrationGroups,
  });
  const groupOptions = groupsQuery.data?.groups ?? [];
  const groupFilterLabel =
    groupFilter === undefined
      ? 'All groups'
      : groupFilter === 'none'
        ? 'No group'
        : (groupOptions.find((g) => g.migrationGroupId === groupFilter)?.name ?? groupFilter);

  // 300ms debounce on the wave filter so every keystroke does not refetch.
  useEffect(() => {
    const handle = window.setTimeout(() => {
      const trimmed = waveInput.trim();
      setWaveFilter(trimmed || undefined);
    }, 300);
    return () => window.clearTimeout(handle);
  }, [waveInput]);

  const query = useInfiniteQuery<MigrationsListResponse>({
    // `migrationGroupId` is IN THE KEY, exactly as the wave filter is. Without
    // it, changing group would keep serving the previously-cached pages — the
    // list would silently show the wrong rows.
    //
    // THE `'list'` SEGMENT IS LOAD-BEARING AND IS NOT DECORATION (FD-485).
    //
    // React Query hashes a key with `JSON.stringify`, which DROPS undefined
    // object properties. So with no filters applied this key hashed to exactly
    // `["migrations",{"pageSize":50}]` — byte-identical to Home's PLAIN
    // `useQuery(['migrations', { pageSize: 50 }])`. One cache entry, two
    // incompatible shapes: `{ pages, pageParams }` from here,
    // `{ migrations, continuationToken }` from there. Whichever page mounted
    // second read the other's data and threw — `pages.length` of undefined
    // inside react-query's `getNextPageParam` on this side, `.migrations.length`
    // of undefined on Home's. An operator saw the error boundary on every
    // navigation between Home and Campaigns, which is every session, because
    // `/` redirects to `/home`.
    //
    // A discriminator no plain query uses makes the collision IMPOSSIBLE rather
    // than merely absent today: a future `['migrations', { … }]` cannot land
    // here by accident. The `'migrations'` prefix is unchanged, so every
    // `invalidateQueries({ queryKey: ['migrations'] })` still matches, and
    // `queryCacheSharing.test.tsx` mounts both pages on one client to prove it.
    queryKey: [
      'migrations',
      'list',
      { migrationWave: waveFilter, migrationGroupId: groupFilter, pageSize: PAGE_SIZE },
    ],
    queryFn: ({ pageParam }) => {
      const params: GetMigrationsParams = {
        pageSize: PAGE_SIZE,
        migrationWave: waveFilter,
        migrationGroupId: groupFilter,
        continuationToken: (pageParam as string | null | undefined) ?? undefined,
      };
      return getMigrations(params);
    },
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.continuationToken ?? undefined,
  });

  const rows = useMemo<Migration[]>(
    () => (query.data ? query.data.pages.flatMap((p) => p.migrations) : []),
    [query.data],
  );

  // The metric row counts the rows LOADED, and the captions say so. A filtered
  // or partially-paged list that presented its counts as totals would be the
  // FD-269 defect class — a number reporting something it cannot know.
  const activeWaves = rows.filter((m) => (m.bprtStatus ?? '').toLowerCase() === 'active').length;
  const completedWaves = rows.filter((m) => (m.bprtStatus ?? '').toLowerCase() === 'revoked').length;
  const deviceTotal = rows.reduce((sum, m) => sum + (m.deviceCount ?? 0), 0);
  const filtered = waveFilter !== undefined || groupFilter !== undefined;

  const columns = useMemo<DataTableColumn<Migration>[]>(
    () => [
      {
        key: 'migrationName',
        header: 'Campaign',
        render: (m) => (
          <span className="font-medium" style={{ color: TEXT.primary }}>
            {m.migrationName}
          </span>
        ),
      },
      {
        key: 'migrationWave',
        header: 'Wave',
        render: (m) => <span style={{ color: TEXT.secondary }}>{m.migrationWave}</span>,
      },
      {
        key: 'migrationType',
        header: 'Type',
        // FD-467 — this column rendered the RAW wire value (`D2C` / `T2T`) while
        // every other campaign surface rendered Hybrid / Cloud. That is exactly
        // the half-applied rename FD-ADR-031 §3 warns about, and the check for
        // it is a grep rather than a reread. The mapping stays in one module.
        render: (m) => (
          <span style={{ color: TEXT.secondary }}>{migrationTypeLabel(m.migrationType)}</span>
        ),
      },
      {
        key: 'migrationGroup',
        header: 'Group',
        // "No group" in neutral text, never an em-dash: "—" reads as DATA
        // MISSING, "No group" reads as A STATE — and it is the ordinary state
        // of most migrations. An unresolvable id is its own third state.
        render: (m) => (
          <GroupLabel
            migrationGroupId={m.migrationGroupId}
            migrationGroupName={m.migrationGroupName}
          />
        ),
      },
      {
        key: 'tenants',
        header: 'Tenants',
        // T2T source → destination, domain label preferred (FD-110). "—" for
        // D2C and any record without tenants.
        render: (m) =>
          m.sourceTenantId || m.sourceTenantDomain || m.destTenantId || m.destTenantDomain ? (
            <span className="inline-flex items-center gap-1" style={{ color: TEXT.secondary }}>
              <TenantLabel domain={m.sourceTenantDomain} tenantId={m.sourceTenantId} />
              <ArrowRight size={12} strokeWidth={1.5} style={{ color: TEXT.disabled }} />
              <TenantLabel domain={m.destTenantDomain} tenantId={m.destTenantId} />
            </span>
          ) : (
            <span style={{ color: TEXT.disabled }}>—</span>
          ),
      },
      {
        key: 'deviceCount',
        header: 'Devices',
        numeric: true,
        align: 'right',
        render: (m) => <span>{m.deviceCount.toLocaleString()}</span>,
      },
      {
        key: 'state',
        header: 'State',
        align: 'right',
        // The wave state, derived — never `Migrations.Status`, which is written
        // "active" at creation and never updated (FD-269).
        render: (m) => {
          const status = deriveCampaignStatus(m);
          return <StatePill state={status.state}>{status.label}</StatePill>;
        },
      },
      {
        key: 'createdAt',
        header: 'Created',
        numeric: true,
        render: (m) => <RelativeTime iso={m.createdAt} />,
      },
      {
        key: 'migrationId',
        header: 'Campaign ID',
        numeric: true,
        render: (m) => (
          <span title={m.migrationId} style={IDENTIFIER}>
            {shortenId(m.migrationId)}
          </span>
        ),
      },
    ],
    [],
  );

  const errorNode = query.isError ? (
    <MessageBar intent="error">
      <MessageBarBody>
        <MessageBarTitle>Could not load campaigns</MessageBarTitle>
        {errorMessage(query.error)}
      </MessageBarBody>
      <Button
        size="small"
        appearance="subtle"
        icon={<RefreshCw size={14} strokeWidth={1.5} />}
        onClick={() => query.refetch()}
      >
        Retry
      </Button>
    </MessageBar>
  ) : undefined;

  const emptyNode = (
    <MessageBar intent="info">
      <MessageBarBody>
        <MessageBarTitle>No campaigns yet</MessageBarTitle>
        {waveFilter
          ? `No campaigns match wave "${waveFilter}".`
          : groupFilter
            ? 'No campaigns in this group.'
            : 'Use New campaign above to create the first migration campaign.'}
      </MessageBarBody>
      {/* No CTA here. The toolbar's "New campaign" is always on screen, so a
          second one inside the empty state is two buttons with one job — and
          an ambiguous accessible name for anything driving the page by role. */}
    </MessageBar>
  );

  return (
    <>
      <HeroBand
        eyebrow="Migration"
        title="Campaigns"
        subtitle="Every migration campaign in this environment."
      />

      <MetricRow>
        <MetricCard
          label="Campaigns loaded"
          value={rows.length.toLocaleString()}
          caption={query.hasNextPage ? 'More pages available' : filtered ? 'Matching the filters' : 'All of them'}
          icon={<Folder size={18} strokeWidth={1.5} />}
        />
        <MetricCard
          label="Active waves"
          value={activeWaves.toLocaleString()}
          tone="progress"
          icon={<PlayCircle size={18} strokeWidth={1.5} />}
        />
        <MetricCard
          label="Completed waves"
          value={completedWaves.toLocaleString()}
          tone="success"
          icon={<CheckCircle2 size={18} strokeWidth={1.5} />}
        />
        <MetricCard
          label="Devices assigned"
          value={deviceTotal.toLocaleString()}
          caption="Across the campaigns loaded"
          icon={<Laptop size={18} strokeWidth={1.5} />}
        />
      </MetricRow>

      <SectionPanel
        action={
          <div className="flex flex-wrap items-center gap-3">
            {/* FD-279: the placeholder was "e.g. NYC Wave 1" — a retired wave value,
                and a campaign NAME at that. The filter itself stays free text on
                purpose: legacy Wave 1..3 campaigns still exist and must remain
                filterable, which a dropdown built from the narrowed MIGRATION_WAVES
                would prevent. */}
            <Input
              id="wave-filter"
              placeholder="Filter by wave — e.g. Pilot"
              aria-label="Filter by wave"
              value={waveInput}
              onChange={(_, data) => setWaveInput(data.value)}
              contentBefore={<Search size={14} strokeWidth={1.5} />}
              style={{ minWidth: 220 }}
            />
            <Dropdown
              id="group-filter"
              aria-label="Filter by group"
              value={groupFilterLabel}
              selectedOptions={[groupFilter ?? GROUP_FILTER_ALL]}
              style={{ minWidth: 180 }}
              onOptionSelect={(_, data) => {
                const next = data.optionValue ?? GROUP_FILTER_ALL;
                const params = new URLSearchParams(searchParams);
                if (next === GROUP_FILTER_ALL) params.delete('group');
                else params.set('group', next);
                setSearchParams(params, { replace: true });
              }}
            >
              <Option value={GROUP_FILTER_ALL} text="All groups">
                All groups
              </Option>
              {/* "No group" is a FIRST-CLASS option, not an afterthought: it is
                  the only way to review the unfiled set, which group detail
                  structurally cannot answer. */}
              <Option value="none" text="No group">
                No group
              </Option>
              {groupOptions.map((g) => (
                <Option key={g.migrationGroupId} value={g.migrationGroupId} text={g.name}>
                  {g.name}
                </Option>
              ))}
            </Dropdown>
            <Button
              appearance="primary"
              icon={<Plus size={14} strokeWidth={1.5} />}
              onClick={() => navigate('/migrations/new')}
            >
              New campaign
            </Button>
          </div>
        }
        bodyClassName="px-0 pb-0"
      >
        <div className="overflow-x-auto">
          <DataTable
            columns={columns}
            data={rows}
            keyForRow={(m) => m.migrationId}
            onRowClick={(m) => navigate(`/migrations/${encodeURIComponent(m.migrationId)}/overview`)}
            isLoading={query.isLoading}
            error={errorNode}
            emptyState={emptyNode}
          />
        </div>
        {query.hasNextPage && !query.isError && (
          <div
            className="flex justify-center border-t py-4"
            style={{ borderColor: STROKE.subtle }}
          >
            <Button
              appearance="secondary"
              onClick={() => query.fetchNextPage()}
              disabled={query.isFetchingNextPage}
            >
              {query.isFetchingNextPage ? 'Loading…' : 'Load more'}
            </Button>
          </div>
        )}
      </SectionPanel>
    </>
  );
}

function shortenId(id: string) {
  if (id.length <= 10) return id;
  return `${id.slice(0, 8)}…`;
}

function errorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  return 'Unexpected error';
}
